import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useMarkOrderedPO } from '@/hooks/usePOMutations'
import { usePO } from '@/hooks/usePOs'
import { useUIStore } from '@/store/uiStore'

const markOrderedSchema = z.object({
  po_number: z.string(),
  // Optional — if provided, must be a valid ISO date.
  expected_delivery: z
    .string()
    .refine((v) => v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v), {
      message: 'Must be a valid date',
    }),
  send_to_vendor: z.boolean(),
})

type MarkOrderedValues = z.infer<typeof markOrderedSchema>

const EMPTY: MarkOrderedValues = {
  po_number: '',
  expected_delivery: '',
  send_to_vendor: false,
}

export function MarkOrderedModal() {
  const activeModal = useUIStore((s) => s.activeModal)
  const activePO = useUIStore((s) => s.activePO)
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const open = activeModal === 'markOrdered'

  const { data: po } = usePO(open ? activePO : null)
  const mutation = useMarkOrderedPO()
  const busy = mutation.isPending

  const form = useForm<MarkOrderedValues>({
    resolver: zodResolver(markOrderedSchema),
    defaultValues: EMPTY,
  })

  React.useEffect(() => {
    if (open) {
      // Pre-fill expected_delivery from the PO if already set in the wizard
      form.reset({
        ...EMPTY,
        expected_delivery: po?.expected_delivery_date?.slice(0, 10) ?? '',
      })
      mutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // If PO loads after modal opens (stale cache case), pre-fill without
  // overwriting anything the user already typed.
  const deliveryFromPO = po?.expected_delivery_date?.slice(0, 10) ?? ''
  const currentDelivery = form.watch('expected_delivery')
  React.useEffect(() => {
    if (deliveryFromPO && !currentDelivery) {
      form.setValue('expected_delivery', deliveryFromPO)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryFromPO])

  function close() {
    setActiveModal(null)
  }

  async function onSubmit(values: MarkOrderedValues) {
    if (!activePO) return
    try {
      const result = await mutation.mutateAsync({
        po_id: activePO,
        po_number: values.po_number,
        expected_delivery: values.expected_delivery,
        send_to_vendor: values.send_to_vendor,
      })
      const label = result.po_id
      const statusLabel =
        result.status === 'PARTIAL_ORDERED'
          ? `partially ordered (some Shopify items failed sync)`
          : 'marked ordered'
      toast.success(`PO ${label} ${statusLabel}`)
      if (result.status === 'PARTIAL_ORDERED') {
        toast.warning('Some items failed to sync to Shopify. Use Retry Sync from the PO menu once resolved.', {
          duration: 6000,
        })
      }
      close()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to mark PO ordered')
    }
  }

  const rawPoNum = (po?.po_number ?? '').replace(/^#+/, '')
  const poLabel = rawPoNum ? `#${rawPoNum}` : activePO ?? ''
  const recipient = (po?.supplier ?? '').trim() || (po?.vendor ?? '').trim()

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && close()}>
      <DialogContent
        className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] flex flex-col gap-0 overflow-hidden p-0 sm:max-w-[480px]"
        showCloseButton={false}
      >
        <DialogHeader className="border-b border-[var(--line-warm)] px-5 py-3.5 sm:text-left">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-base font-semibold">
              Mark ordered
            </DialogTitle>
            {poLabel && (
              <span className="text-xs text-[var(--mute)]">{poLabel}</span>
            )}
            {recipient && (
              <span className="text-xs text-[var(--mute)]">· {recipient}</span>
            )}
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={close}
              disabled={busy}
              aria-label="Close"
            >
              ✕
            </Button>
          </div>
          <DialogDescription className="sr-only">
            Enter the vendor&apos;s PO number and expected delivery date to mark
            this purchase order as ordered.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col gap-4 p-5"
          >
            {po?.is_kentucky && (
              // Guardrail: this modal is now reachable for Kentucky POs (as the
              // "don't send" escape hatch), so make it explicit that it does NOT
              // transmit the order to Kentucky Horsewear.
              <div className="rounded-md border border-[var(--line-warm)] px-3 py-2 text-xs text-[var(--mute)]">
                <span className="font-semibold text-[var(--status-awaiting-fg)]">Won’t send to Kentucky.</span>{' '}
                This marks the PO ordered and books incoming stock in Shopify only — use
                “Order with Kentucky” instead to transmit the order to Kentucky Horsewear.
              </div>
            )}

            <FormField
              control={form.control}
              name="po_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendor&apos;s PO number <span className="font-normal text-[var(--mute)]">(optional)</span></FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. EC-2026-0042"
                      disabled={busy}
                      data-testid="po-number-input"
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="expected_delivery"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expected delivery</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      disabled={busy}
                      data-testid="expected-delivery-input"
                    />
                  </FormControl>
                  <p className="text-xs text-[var(--mute)] mt-0.5">Optional</p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="send_to_vendor"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start gap-2.5">
                  <FormControl>
                    <input
                      id="send-to-vendor"
                      type="checkbox"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={busy}
                      className="h-4 w-4 cursor-pointer mt-0.5"
                      data-testid="send-to-vendor-checkbox"
                    />
                  </FormControl>
                  <div className="leading-none">
                    <FormLabel htmlFor="send-to-vendor" className="cursor-pointer">
                      Notify vendor
                    </FormLabel>
                    <p className="text-xs text-[var(--mute)] mt-0.5">
                      Send an email confirmation to the vendor when marked ordered
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-1 border-t border-[var(--line-warm)]">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={close}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={busy}
                data-testid="mark-ordered-submit"
              >
                {busy ? 'Marking ordered…' : 'Mark ordered'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
